import React, { useMemo, useRef, useState } from 'react';
import './styles.scss';

type VoucherField = {
  fieldId: number;
  fieldLabel: string;
  fieldCode: string;
  value: string;
};

type VoucherApiItem = {
  id: number;
  code: string;
  conversionId: number | null;
  fields: VoucherField[];
};

type VoucherTableRow = VoucherApiItem & {
  children?: VoucherApiItem[];
};

type VoucherColumn<T> = {
  id: string;
  label: string;
  width?: number;
  minWidth?: number;
  render: (row: T) => React.ReactNode;
};

type VoucherTableProps = {
  data?: VoucherApiItem[];
};

const getFieldValue = (row: VoucherApiItem, fieldCode: string, fallback = '') => {
  return row.fields.find(f => f.fieldCode === fieldCode)?.value ?? fallback;
};

const buildVoucherTree = (items: VoucherApiItem[]): VoucherTableRow[] => {
  const groupMap = new Map<number, VoucherApiItem[]>();
  items.forEach(item => {
    const group = groupMap.get(item.id) ?? [];
    group.push(item);
    groupMap.set(item.id, group);
  });
  return Array.from(groupMap.values()).map(group => {
    const parent =
      group.find(item => item.conversionId === null) ?? group[0];
    const children = group.filter(
      item => item !== parent && item.conversionId !== null,
    );
    return { ...parent, children };
  });
};

const DEFAULT_DATA: VoucherApiItem[] = [
  {
    id: 247,
    code: 'VOU2026042402101130000',
    conversionId: 2,
    fields: [
      { fieldId: 1, fieldLabel: 'Tên khách hàng/ Tổ chức', fieldCode: 'cus_name', value: 'admin' },
      { fieldId: 2, fieldLabel: 'Điện thoại', fieldCode: 'cus_tel', value: '0986285167' },
      { fieldId: 34, fieldLabel: 'Số khung (VIN)', fieldCode: 'own_chassis_no', value: 'Không xác định' },
      { fieldId: 35, fieldLabel: 'Biển số xe', fieldCode: 'own_number_plate', value: 'Không xác định' },
      { fieldId: 77, fieldLabel: 'Số hóa đơn', fieldCode: 'repair_invoice_no', value: 'Không xác định' },
      { fieldId: 127, fieldLabel: 'Số hóa đơn', fieldCode: 'trans_invoice_no', value: 'Không xác định' },
      { fieldId: 232, fieldLabel: 'Tên chiến dịch', fieldCode: 'name_campaign', value: 'voucher cho nhóm ' },
      { fieldId: 233, fieldLabel: 'Trạng thái voucher', fieldCode: 'status_voucher', value: 'Đã đổi 1/2' },
      { fieldId: 234, fieldLabel: 'Ngày phát hành voucher', fieldCode: 'voucher_issuance_date', value: 'Không xác định' },
      { fieldId: 235, fieldLabel: 'Ngày quy đổi voucher', fieldCode: 'voucher_conversion', value: '08-05-2026' },
      { fieldId: 236, fieldLabel: 'Đơn vị quy đổi voucher', fieldCode: 'unit_voucher_conversion', value: 'THACO AUTO TP.HCM' },
    ],
  },
  {
    id: 247,
    code: 'VOU2026042402101130000',
    conversionId: 3,
    fields: [
      { fieldId: 1, fieldLabel: 'Tên khách hàng/ Tổ chức', fieldCode: 'cus_name', value: 'admin' },
      { fieldId: 2, fieldLabel: 'Điện thoại', fieldCode: 'cus_tel', value: '0986285167' },
      { fieldId: 34, fieldLabel: 'Số khung (VIN)', fieldCode: 'own_chassis_no', value: 'Không xác định' },
      { fieldId: 35, fieldLabel: 'Biển số xe', fieldCode: 'own_number_plate', value: 'Không xác định' },
      { fieldId: 77, fieldLabel: 'Số hóa đơn', fieldCode: 'repair_invoice_no', value: 'Không xác định' },
      { fieldId: 127, fieldLabel: 'Số hóa đơn', fieldCode: 'trans_invoice_no', value: 'Không xác định' },
      { fieldId: 232, fieldLabel: 'Tên chiến dịch', fieldCode: 'name_campaign', value: 'voucher cho nhóm ' },
      { fieldId: 233, fieldLabel: 'Trạng thái voucher', fieldCode: 'status_voucher', value: 'Đã đổi hết' },
      { fieldId: 234, fieldLabel: 'Ngày phát hành voucher', fieldCode: 'voucher_issuance_date', value: 'Không xác định' },
      { fieldId: 235, fieldLabel: 'Ngày quy đổi voucher', fieldCode: 'voucher_conversion', value: '09-05-2026' },
      { fieldId: 236, fieldLabel: 'Đơn vị quy đổi voucher', fieldCode: 'unit_voucher_conversion', value: 'Cty TT/ĐL Phía Nam' },
    ],
  },
  {
    id: 51460,
    code: 'VOU20260519085412600000',
    conversionId: null,
    fields: [
      { fieldId: 1, fieldLabel: 'Tên khách hàng/ Tổ chức', fieldCode: 'cus_name', value: 'Không xác định' },
      { fieldId: 2, fieldLabel: 'Điện thoại', fieldCode: 'cus_tel', value: '0986285167' },
      { fieldId: 34, fieldLabel: 'Số khung (VIN)', fieldCode: 'own_chassis_no', value: 'Không xác định' },
      { fieldId: 35, fieldLabel: 'Biển số xe', fieldCode: 'own_number_plate', value: 'Không xác định' },
      { fieldId: 77, fieldLabel: 'Số hóa đơn', fieldCode: 'repair_invoice_no', value: 'Không xác định' },
      { fieldId: 127, fieldLabel: 'Số hóa đơn', fieldCode: 'trans_invoice_no', value: 'Không xác định' },
      { fieldId: 232, fieldLabel: 'Tên chiến dịch', fieldCode: 'name_campaign', value: 'test tra cứu' },
      { fieldId: 233, fieldLabel: 'Trạng thái voucher', fieldCode: 'status_voucher', value: 'Chưa đổi' },
      { fieldId: 234, fieldLabel: 'Ngày phát hành voucher', fieldCode: 'voucher_issuance_date', value: 'Không xác định' },
      { fieldId: 235, fieldLabel: 'Ngày quy đổi voucher', fieldCode: 'voucher_conversion', value: 'Không xác định' },
      { fieldId: 236, fieldLabel: 'Đơn vị quy đổi voucher', fieldCode: 'unit_voucher_conversion', value: 'Không xác định' },
    ],
  },
  {
    id: 51472,
    code: 'VOU20260519095506610000',
    conversionId: 55,
    fields: [
      { fieldId: 1, fieldLabel: 'Tên khách hàng/ Tổ chức', fieldCode: 'cus_name', value: 'admin' },
      { fieldId: 2, fieldLabel: 'Điện thoại', fieldCode: 'cus_tel', value: '0986285167' },
      { fieldId: 34, fieldLabel: 'Số khung (VIN)', fieldCode: 'own_chassis_no', value: 'Không xác định' },
      { fieldId: 35, fieldLabel: 'Biển số xe', fieldCode: 'own_number_plate', value: 'Không xác định' },
      { fieldId: 77, fieldLabel: 'Số hóa đơn', fieldCode: 'repair_invoice_no', value: 'Không xác định' },
      { fieldId: 127, fieldLabel: 'Số hóa đơn', fieldCode: 'trans_invoice_no', value: 'Không xác định' },
      { fieldId: 232, fieldLabel: 'Tên chiến dịch', fieldCode: 'name_campaign', value: 'Test voucher cá nhân ' },
      { fieldId: 233, fieldLabel: 'Trạng thái voucher', fieldCode: 'status_voucher', value: 'Đã đổi 1/10' },
      { fieldId: 234, fieldLabel: 'Ngày phát hành voucher', fieldCode: 'voucher_issuance_date', value: 'Không xác định' },
      { fieldId: 235, fieldLabel: 'Ngày quy đổi voucher', fieldCode: 'voucher_conversion', value: '08-05-2026' },
      { fieldId: 236, fieldLabel: 'Đơn vị quy đổi voucher', fieldCode: 'unit_voucher_conversion', value: 'THACO AUTO TP.HCM' },
    ],
  },
];

const TestDrag = ({ data = DEFAULT_DATA }: VoucherTableProps) => {
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  // ── Resize state ──────────────────────────────────────────────────────────
  const resizingRef = useRef<{
    columnId: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  // ── Drag-reorder state ────────────────────────────────────────────────────
  const dragSourceRef = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const rows = useMemo(() => buildVoucherTree(data), [data]);

  const baseColumns = useMemo<VoucherColumn<VoucherApiItem>[]>(
    () => [
      {
        id: 'code',
        label: 'Mã voucher',
        width: 280,
        minWidth: 160,
        render: row => row.code,
      },
      {
        id: 'name_campaign',
        label: 'Tên chương trình voucher',
        width: 360,
        minWidth: 220,
        render: row => getFieldValue(row, 'name_campaign', 'Không xác định'),
      },
      {
        id: 'cus_name',
        label: 'Tên khách hàng',
        width: 220,
        minWidth: 160,
        render: row => getFieldValue(row, 'cus_name', 'Không xác định'),
      },
      {
        id: 'cus_tel',
        label: 'Điện thoại',
        width: 180,
        minWidth: 140,
        render: row => getFieldValue(row, 'cus_tel', 'Không xác định'),
      },
      {
        id: 'status_voucher',
        label: 'Trạng thái voucher',
        width: 200,
        minWidth: 160,
        render: row => getFieldValue(row, 'status_voucher', 'Không xác định'),
      },
      {
        id: 'voucher_conversion',
        label: 'Ngày quy đổi',
        width: 180,
        minWidth: 140,
        render: row => getFieldValue(row, 'voucher_conversion', 'Không xác định'),
      },
      {
        id: 'unit_voucher_conversion',
        label: 'Đơn vị quy đổi',
        width: 260,
        minWidth: 180,
        render: row =>
          getFieldValue(row, 'unit_voucher_conversion', 'Không xác định'),
      },
    ],
    [],
  );

  // Column order — stores column ids in display order
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    baseColumns.map(c => c.id),
  );

  // Ordered columns derived from columnOrder
  const columns = useMemo<VoucherColumn<VoucherApiItem>[]>(() => {
    return columnOrder
      .map(id => baseColumns.find(c => c.id === id))
      .filter(Boolean) as VoucherColumn<VoucherApiItem>[];
  }, [columnOrder, baseColumns]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    () =>
      baseColumns.reduce<Record<string, number>>((acc, col) => {
        acc[col.id] = col.width ?? 180;
        return acc;
      }, {}),
  );

  // ── Tree expand ───────────────────────────────────────────────────────────
  const toggleExpand = (rowId: number) => {
    setExpandedIds(prev =>
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId],
    );
  };
  const isExpanded = (rowId: number) => expandedIds.includes(rowId);

  // ── Resize handlers ───────────────────────────────────────────────────────
  const handleResizeStart = (
    event: React.MouseEvent<HTMLDivElement>,
    column: VoucherColumn<VoucherApiItem>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    resizingRef.current = {
      columnId: column.id,
      startX: event.clientX,
      startWidth: columnWidths[column.id] ?? column.width ?? 180,
    };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (event: MouseEvent) => {
    const resizing = resizingRef.current;
    if (!resizing) return;
    const column = columns.find(col => col.id === resizing.columnId);
    const minWidth = column?.minWidth ?? 80;
    const diff = event.clientX - resizing.startX;
    const nextWidth = Math.max(minWidth, resizing.startWidth + diff);
    setColumnWidths(prev => ({ ...prev, [resizing.columnId]: nextWidth }));
  };

  const handleResizeEnd = () => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  // ── Drag-reorder handlers ─────────────────────────────────────────────────
  const handleDragStart = (
    event: React.DragEvent<HTMLTableCellElement>,
    columnId: string,
  ) => {
    // Don't start a drag if the user is resizing
    if (resizingRef.current) {
      event.preventDefault();
      return;
    }
    dragSourceRef.current = columnId;
    event.dataTransfer.effectAllowed = 'move';
    // Minimal ghost image so the default browser ghost doesn't obscure the indicator
    event.dataTransfer.setDragImage(event.currentTarget, 0, 0);
  };

  const handleDragOver = (
    event: React.DragEvent<HTMLTableCellElement>,
    columnId: string,
  ) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragSourceRef.current && dragSourceRef.current !== columnId) {
      setDragOverId(columnId);
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = (
    event: React.DragEvent<HTMLTableCellElement>,
    targetId: string,
  ) => {
    event.preventDefault();
    const sourceId = dragSourceRef.current;
    if (!sourceId || sourceId === targetId) {
      setDragOverId(null);
      return;
    }
    setColumnOrder(prev => {
      const next = [...prev];
      const fromIdx = next.indexOf(sourceId);
      const toIdx = next.indexOf(targetId);
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, sourceId);
      return next;
    });
    dragSourceRef.current = null;
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    dragSourceRef.current = null;
    setDragOverId(null);
  };

  // ── Cell renderer ─────────────────────────────────────────────────────────
  const renderCellContent = (
    row: VoucherApiItem,
    column: VoucherColumn<VoucherApiItem>,
    options?: {
      hasChildren?: boolean;
      expanded?: boolean;
      isChild?: boolean;
      onToggle?: () => void;
    },
  ) => {
    const isCodeColumn = column.id === 'code';
    if (!isCodeColumn) return column.render(row);

    return (
      <div className={`voucher-code-cell ${options?.isChild ? 'is-child' : ''}`}>
        {options?.hasChildren ? (
          <button
            type="button"
            className={`tree-toggle ${options.expanded ? 'is-open' : ''}`}
            onClick={event => {
              event.stopPropagation();
              options.onToggle?.();
            }}
          >
            <span />
          </button>
        ) : (
          <span className="tree-toggle-placeholder" />
        )}
        <span className="voucher-code-text">{column.render(row)}</span>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="voucher-table-wrapper">
      <table className="voucher-table">
        <colgroup>
          {columns.map(col => (
            <col key={col.id} style={{ width: columnWidths[col.id] }} />
          ))}
        </colgroup>

        <thead>
          <tr>
            {columns.map((col, i) => {
              const isDragOver = dragOverId === col.id;
              const isDragging = dragSourceRef.current === col.id;
              return (
                <th
                  key={col.id}
                  className={[
                    'dt-cell dt-th bg-096DD9 text-white h-45px',
                    isDragOver ? 'col-drag-over' : '',
                    isDragging ? 'col-dragging' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    width: columnWidths[col.id],
                    minWidth: col.minWidth,
                  }}
                  draggable
                  onDragStart={e => handleDragStart(e, col.id)}
                  onDragOver={e => handleDragOver(e, col.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, col.id)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="dt-th-inner">
                    <span className="dt-th-text text-left text-white">
                      {col.label}
                    </span>
                  </div>

                  {/* Resize handle — last column has none */}
                  <div
                    className={`resize-handle ${columns.length === i + 1 ? 'd-none' : ''}`}
                    onMouseDown={event => handleResizeStart(event, col)}
                  />
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {rows.map(row => {
            const hasChildren = !!row.children?.length;
            const expanded = isExpanded(row.id);
            return (
              <React.Fragment key={row.id}>
                <tr className="voucher-row voucher-parent-row">
                  {columns.map(col => (
                    <td
                      key={col.id}
                      style={{
                        width: columnWidths[col.id],
                        minWidth: col.minWidth,
                      }}
                    >
                      {renderCellContent(row, col, {
                        hasChildren,
                        expanded,
                        onToggle: () => toggleExpand(row.id),
                      })}
                    </td>
                  ))}
                </tr>

                {expanded &&
                  row.children?.map(child => (
                    <tr
                      key={`${child.id}-${child.conversionId}`}
                      className="voucher-row voucher-child-row"
                    >
                      {columns.map(col => (
                        <td
                          key={col.id}
                          style={{
                            width: columnWidths[col.id],
                            minWidth: col.minWidth,
                          }}
                        >
                          {renderCellContent(child, col, { isChild: true })}
                        </td>
                      ))}
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TestDrag;
